import React from 'react';

import { Divider, Grid, Header } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';
import { useUpdateConvention } from '@/hooks';
import {
  formatDate,
  formatLieu,
  normalizeConventionCodes,
  resolveCasCode,
} from '@/utils';
import { MOTIFS_DISSOLUTION } from '@/constants';
import type { CasCode } from '@/constants/casConventionCodes';

import InformationsDissolutionModal from './InformationsDissolutionModal';

interface InformationsDissolutionProps {
  conventionDeclaration: any;
  onSaved?: () => void;
}

const InformationsDissolution = (
  props: InformationsDissolutionProps,
): JSX.Element => {
  const { conventionDeclaration, onSaved } = props;

  const { updateConvention } = useUpdateConvention();

  // Brouillon local (édition en une seule modale) persisté par un PUT de la
  // convention complète à la sauvegarde.
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [infoDissolution, setInfoDissolution] = React.useState<any>(null);

  const handleOpen = React.useCallback(() => {
    // Le GET renvoie typeDissolution en objet { identifiant, libelle } ; on en
    // extrait la clé pour le PUT (fallback : résolution du libellé).
    const identifiantTypeDissolution =
      conventionDeclaration?.identifiantTypeDissolution ??
      conventionDeclaration?.typeDissolution?.identifiant ??
      resolveCasCode(
        MOTIFS_DISSOLUTION as unknown as CasCode[],
        conventionDeclaration?.typeDissolution?.libelle,
      );

    setInfoDissolution({
      identifiantTypeDissolution,
      dateMariage: conventionDeclaration?.dateMariage ?? null,
      partenaire1DateDeces: conventionDeclaration?.partenaire1DateDeces ?? null,
      partenaire2DateDeces: conventionDeclaration?.partenaire2DateDeces ?? null,
      dateDeclarationUnitDi:
        conventionDeclaration?.dateDeclarationUnitDi ?? null,
      dateSignification: conventionDeclaration?.dateSignification ?? null,
      dateDeclarationJointDi:
        conventionDeclaration?.dateDeclarationJointDi ?? null,
      dateDissolution: conventionDeclaration?.dateDissolution ?? null,
      adresseDissolution: { ...conventionDeclaration?.adresseDissolution },
    });
    setModalOpen(true);
  }, [conventionDeclaration]);

  const handleChange = React.useCallback((data: Record<string, unknown>) => {
    setInfoDissolution((prev: any) => ({ ...prev, ...data }));
  }, []);

  const handleClose = React.useCallback(() => {
    setModalOpen(false);
    setInfoDissolution(null);
  }, []);

  const handleSave = React.useCallback(() => {
    if (!conventionDeclaration?.identifiant) {
      return Promise.reject(
        new Error('Contexte de convention incomplet pour la sauvegarde'),
      );
    }

    const payload = {
      ...normalizeConventionCodes(conventionDeclaration),
      ...infoDissolution,
    };

    return updateConvention(conventionDeclaration.identifiant, payload).then(
      () => {
        onSaved?.();
      },
    );
  }, [conventionDeclaration, infoDissolution, updateConvention, onSaved]);

  const adresseDissolution = formatLieu(
    conventionDeclaration?.adresseDissolution?.localite,
    conventionDeclaration?.adresseDissolution?.codePostal,
    conventionDeclaration?.adresseDissolution?.libellePays,
  );

  const champsCausesDissolution = React.useMemo(() => {
    const libelle = conventionDeclaration?.typeDissolution?.libelle;

    if (
      libelle === 'Mariage des partenaires' ||
      libelle === 'Mariage du partenaire 1' ||
      libelle === 'Mariage du partenaire 2'
    ) {
      return (
        <Grid.Column width={5}>
          <span>
            <b>Date de mariage : </b>
          </span>
          <span>{formatDate(conventionDeclaration?.dateMariage)}</span>
        </Grid.Column>
      );
    }

    if (libelle === 'Décès des partenaires') {
      return (
        <>
          <Grid.Column width={5}>
            <span>
              <b>Date de décès du partenaire 1 : </b>
            </span>
            <span>
              {formatDate(conventionDeclaration?.partenaire1DateDeces)}
            </span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>
              <b>Date de décès du partenaire 2 : </b>
            </span>
            <span>
              {formatDate(conventionDeclaration?.partenaire2DateDeces)}
            </span>
          </Grid.Column>
        </>
      );
    }

    if (libelle === 'Décès du partenaire 1') {
      return (
        <Grid.Column width={5}>
          <span>
            <b>Date de décès du partenaire 1 : </b>
          </span>
          <span>{formatDate(conventionDeclaration?.partenaire1DateDeces)}</span>
        </Grid.Column>
      );
    }

    if (libelle === 'Décès du partenaire 2') {
      return (
        <Grid.Column width={5}>
          <span>
            <b>Date de décès du partenaire 2 : </b>
          </span>
          <span>{formatDate(conventionDeclaration?.partenaire2DateDeces)}</span>
        </Grid.Column>
      );
    }

    if (
      libelle === 'Décision unilatérale du partenaire 1' ||
      libelle === 'Décision unilatérale du partenaire 2' ||
      libelle ===
        "Décision unilatérale du partenaire 1 à l'initiative d'un tuteur" ||
      libelle ===
        "Décision unilatérale du partenaire 2 à l'initiative d'un tuteur"
    ) {
      return (
        <>
          <Grid.Column width={5}>
            <span>
              <b>Date de déclaration unilatérale : </b>
            </span>
            <span>
              {formatDate(conventionDeclaration?.dateDeclarationUnitDi)}
            </span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>
              <b>Date de signification : </b>
            </span>
            <span>{formatDate(conventionDeclaration?.dateSignification)}</span>
          </Grid.Column>
        </>
      );
    }

    if (
      libelle === 'Déclaration conjointe des partenaires (commun accord)' ||
      libelle ===
        "Déclaration conjointe des partenaires (commun accord) à l'initiative d'un tuteur"
    ) {
      return (
        <Grid.Column width={5}>
          <span>
            <b>Date de déclaration conjointe : </b>
          </span>
          <span>
            {formatDate(conventionDeclaration?.dateDeclarationJointDi)}
          </span>
        </Grid.Column>
      );
    }

    return null;
  }, [conventionDeclaration]);

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            CAUSE DE DISSOLUTION
          </Header>
          <PanelButtonEdition
            onClick={handleOpen}
            data-testid="panel-edit-modal-dissolution"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          <span>
            <b>Dissolution par : </b>
          </span>
          <span>{conventionDeclaration?.typeDissolution?.libelle}</span>
        </Grid.Column>
        {champsCausesDissolution}
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <Header as="h5" color="blue">
          DATE ET LIEU DE DISSOLUTION
        </Header>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          <span>
            <b>Date dissolution : </b>
          </span>
          <span>{formatDate(conventionDeclaration?.dateDissolution)}</span>
        </Grid.Column>
        <Grid.Column width={5}>
          <span>
            <b>Lieu de dissolution : </b>
          </span>
          <span>{adresseDissolution}</span>
        </Grid.Column>
        <Grid.Column width={5}>
          <span>
            <b>Commune déléguée : </b>
          </span>
          <span>
            {conventionDeclaration?.adresseDissolution?.communeDelegue ||
              'Non renseigné'}
          </span>
        </Grid.Column>
      </Grid.Row>
      {modalOpen && (
        <InformationsDissolutionModal
          data={infoDissolution}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleClose}
          onCancel={handleClose}
        />
      )}
    </Grid>
  );
};

export default InformationsDissolution;
